import React from 'react';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { getC2CTransactions } from '~/javascripts/admin/actions/transactions';
import { formatDate, formatUUID } from '~/javascripts/admin/utils';
import { createBadge } from '../../atoms/Badge';
import Table from '../../atoms/Table';
import SeqPager from '../../organisms/SeqPager';

type Props = {
  c2cTransfers?: Awaited<ReturnType<typeof getC2CTransactions>>;
  onChangePage: (paginatedLink: string | null) => void;
};

type Header = {
  label: string;
  size: number;
  align?: 'center' | 'justify' | 'left' | 'right';
  tooltipKey?: string;
  tooltipText?: string | React.ReactNode;
};

const headers: Header[] = [
  { label: '種別', size: 2 },
  {
    label: '取引ID',
    size: 2,
  },
  { label: '日時', size: 2 },
  { label: '送付元', size: 2 },
  { label: '送付先', size: 2 },
  { label: 'マネー', size: 2, align: 'right' },
];

const TooltipTableCell = styled(Table.Cell)<{ tooltip?: boolean }>`
  &:hover {
    ${({ tooltip }) => tooltip && 'cursor: pointer; background-color: #eeeeee;'}
  }
`;

const C2CTransferBadge = createBadge({
  transfer: { label: 'マネー譲渡', bgColor: '#E6AE06' },
});

const ListTable = ({ c2cTransfers, onChangePage }: Props) => {
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          {headers.map((c) => (
            <TooltipTableCell
              size={c.size}
              key={c.label}
              align={c.align}
              tooltip={!!c.tooltipKey && !!c.tooltipText}
              data-tip
              data-for={c.tooltipKey}
            >
              {c.label}
              {!!c.tooltipKey && !!c.tooltipText && (
                <ReactTooltip id={c.tooltipKey} type="dark" place="top" effect="solid">
                  <span>{c.tooltipText}</span>
                </ReactTooltip>
              )}
            </TooltipTableCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {c2cTransfers?.rows?.map((t) => (
          <Table.Row key={t.id}>
            <Table.Cell wordBreak="keep-all">
              <C2CTransferBadge status="transfer" />
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">{formatUUID(t.id)}</Table.Cell>
            <Table.Cell wordBreak="keep-all">{formatDate(t.done_at)}</Table.Cell>
            <Table.Cell wordBreak="keep-all">
              {t.sender_account.user.name}
              {'\n'}(
              <Link to={`/customers/${t.sender_account.user.id}`}>
                {formatUUID(t.sender_account.user.id)}
              </Link>
              )
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">
              {t.receiver_account.user.name}
              {'\n'}(
              <Link to={`/customers/${t.receiver_account.user.id}`}>
                {formatUUID(t.receiver_account.user.id)}
              </Link>
              )
            </Table.Cell>
            <Table.Cell align="right">{t.amount.toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row noBorder>
          <Table.Cell colSpan={6} align="center" styles={{ paddingTop: '16px' }}>
            {c2cTransfers && (
              <SeqPager
                prev={c2cTransfers.prev}
                next={c2cTransfers.next}
                onChangePage={onChangePage}
              />
            )}
          </Table.Cell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default ListTable;
